import React, { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage
} from '../../components/ui/Form';
import { Select, SelectItem } from '../../components/ui/Select';
import { INTERCOM_UNIT_NAME_DISPLAY_SETTINGS, INTERCOM_NAME_SETTINGS } from '../../helper/constants';
import { Flex } from '../../components/ui/Flex';
import styled from 'styled-components';
import { Fonts, Palette } from '../../components/utils';
import { IoChevronForward } from 'react-icons/io5';
import { HeaderText, SectionContainer, SectionSubtitle } from '../../components/Modals';

const PREVIEW_NAME = 'John Doe';
const PREVIEW_UNIT = 'Unit 101';

/**
 * Intercom settings form section: resident name display only.
 * Enable Akuvox toggle lives in the Edit Building modal; when enabled, these settings control how names appear on the intercom.
 * @param {Object} props
 * @param {boolean} [props.isAkuvoxEnabled] - When false, name editing fields are hidden and a message is shown.
 */
const IntercomSettingsFormInputs = ({ isAkuvoxEnabled = false }) => {
  const { t } = useTranslation();
  const form = useFormContext();

  const [nameSetting, nameDisplaySetting] = useWatch({
    control: form.control,
    name: ['intercom_name_format', 'intercom_unit_name_display']
  });

  const previewName = useMemo(() => {
    if (!nameSetting || !nameDisplaySetting) return PREVIEW_NAME;
    const nameConfig = Object.values(INTERCOM_NAME_SETTINGS).find(
      (config) => config.value === nameSetting
    );
    const displayConfig = Object.values(INTERCOM_UNIT_NAME_DISPLAY_SETTINGS).find(
      (config) => config.value === nameDisplaySetting
    );
    if (!nameConfig || !displayConfig) return PREVIEW_NAME;
    const formattedName = nameConfig.preview(PREVIEW_NAME);
    return displayConfig.preview(formattedName, PREVIEW_UNIT);
  }, [nameSetting, nameDisplaySetting]);

  return (
    <SectionContainer>
      <HeaderText alt>{t('Intercom Settings')}</HeaderText>
      <SectionSubtitle>
        {t('Configure how resident names appear on the intercom when Akuvox is enabled for this building.')}
      </SectionSubtitle>
      {!isAkuvoxEnabled ? (
        <ExplanationText>
          {t('Change building settings to include Akuvox integration to configure how resident names appear on the intercom.')}
        </ExplanationText>
      ) : (
      <Flex direction="column" gap="16px">
        <FormField
          control={form.control}
          name="intercom_name_format"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Resident name format')}</FormLabel>
              <FormControl>
                <Select
                  placeholder={t('Select resident name format')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  {Object.entries(INTERCOM_NAME_SETTINGS).map(([_, { value, label }]) => (
                    <SelectItem value={value} key={value}>
                      {t(label)}
                    </SelectItem>
                  ))}
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Flex gap="0.125rem" alignItems="center">
          <IoChevronForward size={20} />
          <ExplanationText>
            {t(
              'Controls whether the residents unit number is shown, and where it appears relative to the name.'
            )}
          </ExplanationText>
        </Flex>

        <FormField
          control={form.control}
          name="intercom_unit_name_display"
          render={({ field }) => (
            <FormItem alt>
              <FormLabel v3>{t('Unit number display')}</FormLabel>
              <FormControl>
                <Select
                  placeholder={t('Select unit number display')}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                >
                  {Object.entries(INTERCOM_UNIT_NAME_DISPLAY_SETTINGS).map(
                    ([_, { value, label }]) => (
                      <SelectItem value={value} key={value}>
                        {t(label)}
                      </SelectItem>
                    )
                  )}
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Flex gap="0.125rem" alignItems="center">
          <IoChevronForward size={20} />
          <ExplanationText>
            {t('Whether the unit number is displayed before or after the name.')}
          </ExplanationText>
        </Flex>

        <NameFormatPreview>
          <p>{t('Display name preview')}</p>
          <p>{previewName}</p>
        </NameFormatPreview>
      </Flex>
      )}
    </SectionContainer>
  );
};

const ExplanationText = styled.p`
  margin-bottom: 0;
  font-size: 12px;
  color: ${Palette.HAZY_BLUE};
`;

const NameFormatPreview = styled.div`
  display: flex;
  padding: 16px 24px;
  justify-content: space-between;
  align-items: flex-start;
  align-self: stretch;
  border-radius: 10px;
  background: ${Palette.SOLITUDE};

  p {
    color: ${Palette.BLACK};
    font-family: ${Fonts.OPEN_SANS};
    font-size: 14px;
    font-weight: 400;
    margin: 0;
  }

  p:last-child {
    font-weight: 700;
  }
`;

export default IntercomSettingsFormInputs;
